import { useLayoutEffect, useMemo } from 'react';

import { BRANDING_VAR_KEYS, buildBrandingVars } from '../../helpers/brandColors';

import { PracticeBrandingContext } from './PracticeBrandingContext';

const CACHE_KEY = 'patient-branding';

const readCachedBranding = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const cacheBranding = (branding) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(branding));
  } catch {
    // sessionStorage unavailable — branding just re-fetches next load.
  }
};

const applyVars = (vars) => {
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
};

export const PracticeBrandingContextProvider = ({ branding, children }) => {
  const primaryColor = branding?.primary_color || null;
  const secondaryColor = branding?.secondary_color || null;

  // On mount, synchronously re-apply the last-known branding from sessionStorage so a reload
  // paints the practice theme immediately instead of flashing the default until the API returns.
  useLayoutEffect(() => {
    const cached = readCachedBranding();
    if (cached) applyVars(buildBrandingVars(cached));

    return () => {
      const root = document.documentElement;
      BRANDING_VAR_KEYS.forEach((key) => root.style.removeProperty(key));
    };
  }, []);

  useLayoutEffect(() => {
    const effective = { primary_color: primaryColor, secondary_color: secondaryColor };
    const vars = buildBrandingVars(effective);
    if (!Object.keys(vars).length) return;

    const root = document.documentElement;
    BRANDING_VAR_KEYS.forEach((key) => root.style.removeProperty(key));
    applyVars(vars);
    cacheBranding(effective);
  }, [primaryColor, secondaryColor]);

  const context = useMemo(() => ({ primaryColor, secondaryColor }), [primaryColor, secondaryColor]);

  return (
    <PracticeBrandingContext.Provider value={context}>{children}</PracticeBrandingContext.Provider>
  );
};
